import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { DevolutionDetailModel } from 'src/app/models/rental/devolution-detail.model';
import { DevolutionModel } from 'src/app/models/rental/devolution.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { environment } from 'src/environments/environment';
import { RentalStockModel } from '../../models/rental/rental-stock-response.model';

@Injectable({
  providedIn: 'root'
})
export class DevolutionDetailService {

  constructor(private _httpClient: HttpClient) { }

  areThereBalancesInCostCenter(branchId: number, costCenterId: number): Observable<boolean> {
    const params = new HttpParams().set('branchId', branchId).set('costCenterId', costCenterId);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.AreThereBalancesInCostCenter}`,
      { params: params }
    );
  }

  convertToDevolution(devolution: DevolutionModel): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.ConvertToDevolution}`,
      devolution);
  }

  createDevolutionDetail(devolutionDetailContractModel: DevolutionDetailModel): Observable<DevolutionDetailModel> {
    return this._httpClient.post<DevolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.CreateDevolutionDetail}`,
      devolutionDetailContractModel);
  }

  createSomeDetails(rentalStocks: Array<RentalStockModalModel>, devolutionId: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.CreateSomeDetails}/${devolutionId}`,
      rentalStocks);
  }

  deleteDevolutionDetail(devolutionDetailId: number): Observable<DevolutionDetailModel> {
    return this._httpClient.delete<DevolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.DeleteDevolutionDetail}/${devolutionDetailId}`);
  }

  deleteDevolutionDetailsInZero(devolutionId: number): Observable<boolean> {
    return this._httpClient.delete<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.DeleteDevolutionDetailsInZero}/${devolutionId}`);
  }

  deleteSomeDetails(devolutionDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.DeleteSomeDetails}`,
      devolutionDetailIds);
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.ExportDetailConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  getAllDevolutionDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetAllDevolutionDetailsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getArticlesForDetailsAsync(branchId: number, costCenterId: number, rentalPeriodId: number, page: number, limit: number,
    columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<RentalStockModalModel>> {
    const params = new HttpParams().set('branchId', branchId).set('costCenterId', costCenterId).set('rentalPeriodId', rentalPeriodId)
      .set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<RentalStockModalModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetArticlesForDetailsAsync}`,
      filters, { params: params }
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetColumnsForArticles}`);
  }

  getColumnsForCaptureTable(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetColumnsForCaptureTableAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetColumnsForGeneralConsultationAsync}`);
  }

  getDevolutionDetailById(devolutionDetailId: number): Observable<DevolutionDetailModel> {
    return this._httpClient.get<DevolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetDevolutionDetailById}/${devolutionDetailId}`);
  }

  getDevolutionDetailByKeys(devolutionId: number, articleId: number, storeId: number): Observable<DevolutionDetailModel> {
    const params = new HttpParams().set('devolutionId', devolutionId).set('articleId', articleId).set('storeId', storeId);
    return this._httpClient.get<DevolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetDevolutionDetailByKeys}`,
      { params: params });
  }

  getDevolutionDetailsBySwIncludeStore(devolutionId: number, swIncludeStore: boolean): Observable<Array<DevolutionDetailModel>> {
    return this._httpClient.get<Array<DevolutionDetailModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetDevolutionDetailsBySwIncludeStore}/${devolutionId}/${swIncludeStore}`);
  }

  getDevolutionDetailsForCaptureTable(page: number, limit: number, columnSort: string | null, ascendant: boolean, devolutionId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '')
      .set('ascendant', ascendant).set('devolutionId', devolutionId);
    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetDevolutionDetailsForCaptureTableAsync}`,
      { params: params }
    );
  }

  getPredevolutionDetail(devolution: DevolutionModel): Observable<Array<RentalStockModel>> {
    return this._httpClient.post<Array<RentalStockModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.GetPredevolutionDetail}`,
      devolution);
  }

  savePredevolutionDetails(rentalStock: RentalStockModalModel, devolutionId: number): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.SavePredevolutionDetails}/${devolutionId}`,
      rentalStock);
  }

  updateDevolutionDetail(devolutionDetailContractModel: DevolutionDetailModel): Observable<DevolutionDetailModel> {
    return this._httpClient.put<DevolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolutionDetail.UpdateDevolutionDetail}`,
      devolutionDetailContractModel);
  }
}
